import { onAuthStateChanged, type Auth, type User } from 'firebase/auth';
import { ApiClientError } from '@/lib/api/client';

export const AUTH_STATE_TIMEOUT_MS = 10_000;

/** Wait only for the SDK's existing session restoration; never initiate sign-in. */
export function waitForFirebaseUser(auth: Auth, signal: AbortSignal): Promise<User | null> {
  return new Promise((resolve, reject) => {
    let unsubscribe: (() => void) | undefined;
    let settled = false;
    const cleanup = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', aborted);
      unsubscribe?.();
    };
    const finish = (user: User | null, error?: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error); else resolve(user);
    };
    const aborted = () => finish(null, new DOMException('Session check aborted', 'AbortError'));
    const timer = setTimeout(() => finish(null, new ApiClientError('Authentication state timed out', 408, 'AUTH_STATE_TIMEOUT')), AUTH_STATE_TIMEOUT_MS);
    signal.addEventListener('abort', aborted, { once: true });
    if (signal.aborted) { aborted(); return; }
    try {
      unsubscribe = onAuthStateChanged(auth, (user) => finish(user), (error) => finish(null, error));
      // Test doubles and already-initialized SDK adapters can notify synchronously.
      if (settled) unsubscribe();
    } catch (error) { finish(null, error); }
  });
}

export const AUTH_TOKEN_TIMEOUT_MS = 10_000;

/** Bound an existing user's SDK refresh; the SDK itself cannot be cancelled. */
export function waitForFirebaseToken(user: User, signal: AbortSignal): Promise<string> {
  if (signal.aborted) return Promise.reject(new DOMException('Session check aborted', 'AbortError'));
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (token?: string, error?: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', aborted);
      if (error !== undefined) reject(error); else resolve(token!);
    };
    const aborted = () => finish(undefined, new DOMException('Session check aborted', 'AbortError'));
    const timer = setTimeout(() => finish(undefined, new ApiClientError('Authentication token timed out', 408, 'AUTH_TOKEN_TIMEOUT')), AUTH_TOKEN_TIMEOUT_MS);
    signal.addEventListener('abort', aborted, { once: true });
    try {
      // One existing refresh only. A late result is consumed without starting a login.
      void user.getIdToken(true).then(token => finish(token), error => finish(undefined, error));
    } catch (error) { finish(undefined, error); }
  });
}
